import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Tabs,
  Tab,
  Switch,
  IconButton,
  Select,
  MenuItem,
  Button,
  Tooltip,
} from "@mui/material";
import TeamList from "../../features/manageMaintenanceTeam/teamList";
import TeamTopbar from "../../features/manageMaintenanceTeam/topbar";
import { GridColDef } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import { isValidInput } from "../../utils/validations";
import SearchBar from "../../components/SearchBar";
import {
  useLazyDeleteEmployerQuery,
  useLazyGetEmployersQuery,
  useLazyUserExportQuery,
  useUpdateApproveStatusMutation,
  useUpdateStatusCustomerMutation,
  useUpdateApproveStatusFreelancerMutation,
} from "../../services/users";
import { Employer } from "../../types/General";
import { showError, showToast, showWarning } from "../../constants/toasts";
import { APPROVED_STATUS, ROLES } from "../../utils/enums";
import TableComponent from "../../components/TableComponent";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import usePermission from "../../hooks/usePermission";
import useTranslation from "../../hooks/traslation";
import RejectReasonModal from "../../Modals/RejectionReason";
import FreelancerRejectionReasonModal from "../../Modals/FreelancerRejectionReason";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const styles = {
  tableFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#fff',
    borderTop: '1px solid rgba(224, 224, 224, 1)',
  },
  paginationInfo: {
    fontSize: '14px',
    color: 'rgba(0, 0, 0, 0.87)',
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  totalRecords: {
    color: 'rgba(0, 0, 0, 0.6)',
  }
};

const ManageMaintenanceTeam = () => {
  const navigate = useNavigate();
  const translation = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "";
  const pageNumber = searchParams.get("page") || "";

  const [employersList] = useLazyGetEmployersQuery();
  const [updateStatus] = useUpdateStatusCustomerMutation();
  const [updateAccountStatius] = useUpdateApproveStatusMutation();
  const [updateFreelancerStatus] = useUpdateApproveStatusFreelancerMutation();
  const [deleteMethod] = useLazyDeleteEmployerQuery();
  const [exportCSV] = useLazyUserExportQuery();
  const { isAdd, isDelete } = usePermission({
    label: "Manage Freelancers",
    page_permission: "isView",
  });

  // const [status, setStatus] = useState("default");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [rejectionRsn, setRejectionRsn] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [openRejection, setOpenRejection] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState<Employer[]>([]);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  const [valueTabs, setValueTabs] = useState(tab ? Number(tab) : 0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setPage(1);
    setSearchParams({ tab: String(newValue) });
  };

  const getExportCsv = async () => {
    try {
      const response = await exportCSV({
        page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
        role: ROLES.FREELANCER,
        status: valueTabs + 1,
      }).unwrap();
      if (response?.statusCode === 200) {
        window.open(response?.data || "");
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // const handleStatusUpdate = async (id: string, status: boolean) => {
  //   const body = {
  //     isBlocked: status,
  //   };

  //   try {
  //     const response = await updateStatus({ id, body }).unwrap();
  //     if (response?.statusCode === 200) {
  //       const updatedData = rows?.map((item) => {
  //         if (item?._id === id) {
  //           return {
  //             ...item,
  //             isBlocked: status,
  //           };
  //         }
  //         return item;
  //       });
  //       setRows(updatedData);
  //       if (status === true) {
  //         showToast("Freelancer deactivated successfully");
  //       } else {
  //         showToast("Freelancer activated successfully");
  //       }
  //     } else {
  //       showError("Please try again");
  //     }
  //   } catch (error: any) {
  //     console.log(error, "errror");
  //     showError(error?.data?.message || "");
  //   }
  // };

  const handleApprove = async (id: string) => {
    const body = {
      approveStatus: APPROVED_STATUS.APPROVED,
    };

    try {
      const response = await updateAccountStatius({ id, body }).unwrap();
      if (response?.statusCode === 200) {
        showToast("Freelancer approved successfully");
        getAllEmployers();
      } else {
        showError(translation.error.tryAgain);
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const handleApproveReject = async (id: string, approveStatus: number) => {
    setSelectedId(id);

    if (approveStatus === APPROVED_STATUS.REJECTED) {
      setOpenRejection(true);
      return;
    }

    if (approveStatus === APPROVED_STATUS.APPROVED) {
      handleApprove(id);
      return;
    }
  };

  const handleReject = async (
    id: string,
    approveStatus: number,
    reason: string
  ) => {
    if (!reason) {
      showWarning("Please add rejection reason");
      return;
    }

    const body = {
      approveStatus,
      rejectReason: reason,
    };

    try {
      const response = await updateFreelancerStatus({ id, body }).unwrap();
      if (response?.statusCode === 200) {
        setRejectionRsn("");
        setOpenRejection(false);
        showToast("Freelancer rejected successfully");
        getAllEmployers();
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const getAllEmployers = async () => {
    setIsLoading(true);
    try {
      const response = await employersList({
        page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
        role: ROLES.FREELANCER,
        approveStatus: valueTabs + 1,
      }).unwrap();
      if (response?.statusCode === 200) {
        setIsLoading(false);
        setRows(response?.data?.list || []);
        setCount(response?.data?.totalCount);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);

    try {
      const response = await deleteMethod({ id: selectedId || "" }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast(translation.error.freelancerDelete);
        getAllEmployers();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllEmployers();
  }, [debouncedSearchTerm, page, valueTabs]);



  const label = { inputProps: { "aria-label": "Switch demo" } };

  const pendingColumns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 240,
      field: "name",
      headerName: translation.globals.name,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() => navigate(`/manage-freelancer/detail/${row?._id}`)}
          >
            <figure>
              <img
                src={row?.image || "/static/images/user_placeholder.png"}
                alt=""
              />
            </figure>
            <p>
              <b>
                {row?.firstName || "-"} {row?.lastName || ""}
              </b>{" "}
              <span>{row?.email || ""}</span>
              <span>
                {row?.dialCode || "-"} {row?.phoneNo || "-"}
              </span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 160,
      field: "phone",
      headerName: translation.main.phone,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div>
            <p>{row?.dialCode || "-"} {row?.phoneNo || "-"}</p>
          </div>
        );
      },
    },
    {
      minWidth: 150,
      field: "ongoingJobs",
      headerName: translation.main.totalTasks,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p>
            <strong> {row?.totalTasks || "0"}</strong>
          </p>
        );
      },
    },
    {
      minWidth: 150,
      field: "completedTasks",
      headerName: translation.main.comJobs,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p>
            <strong> {row?.completedTasks || "0"}</strong>
          </p>
        );
      },
    },

    {
      minWidth: 150,
      field: "earnings",
      headerName: translation.main.totalEarning,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p className="c_success">
            <strong>AED {row?.totalEarnings || "0"}</strong>
          </p>
        );
      },
    },
    {
      minWidth: 180,
      field: "statdsus",
      headerName: translation.main.AccountStatus,
      editable: false,
      sortable: false,
      renderCell: ({ row }: any) => {
        return (
          <Select
            className="select_div"
            fullWidth
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={row?.approvedStatus ?? "default"} // fallback to 'default'
            onChange={(e: any) => handleApproveReject(row?._id, e.target.value)}
            inputProps={{
              "aria-label": "Without label",
            }}
            displayEmpty
          >
            <MenuItem disabled value="default">
              {translation.main.selectStatus}
            </MenuItem>
            <MenuItem value={APPROVED_STATUS.APPROVED}>
              {translation.main.Approve}
            </MenuItem>
            <MenuItem value={APPROVED_STATUS.REJECTED}>
              {translation.main.Reject}
            </MenuItem>
          </Select>
        );
      },
    },
    // {
    //   minWidth: 100,
    //   field: "status",
    //   headerName: translation.globals.status,
    //   editable: false,
    //   sortable: false,
    //   align: "center",
    //   headerAlign: "center",
    //   renderCell: ({ row }) => (
    //     <Switch
    //       {...label}
    //       size="small"
    //       checked={!row?.isBlocked}
    //       onChange={() => handleStatusUpdate(row?._id, !row?.isBlocked)}
    //     />
    //   ),
    // },
    {
      minWidth: 130,
      field: "action",
      headerName: translation.globals.actions,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions v2">
            <Box>
              <Tooltip title="View">
                <IconButton
                  onClick={() =>
                    navigate(
                      `/manage-freelancer/detail/${row?._id}?page=${page}`
                    )
                  }
                >
                  <img src="/static/images/eye_icon.svg" alt="" />
                </IconButton>
              </Tooltip>
            </Box>
            {isAdd ? (
              <Box>
                <Tooltip title="Edit">
                  <IconButton
                    onClick={() =>
                      navigate(`/manage-freelancer/edit/${row?._id}`)
                    }
                  >
                    <img src="/static/images/edit2_icon.svg" alt="" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}

            {isDelete ? (
              <Box>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => {
                      setSelectedId(row?._id);
                      setOpenDelete(true);
                    }}
                  >
                    <img src="/static/images/trash2_icon.svg" alt="" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
          </div>
        );
      },
    },
  ];

  const handlePageChange = (event: any, newPage: number) => {
    const updatedPage = newPage + 1;
    setPage(updatedPage);

    const params = new URLSearchParams(window.location.search);
    params.set("page", updatedPage.toString());

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const ApprovedColumns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 240,
      field: "name",
      headerName: translation.globals.name,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(`/manage-freelancer/detail/${row?._id}?page=${page}`)
            }
          >
            <figure>
              <img
                src={row?.image || "/static/images/user_placeholder.png"}
                alt=""
              />
            </figure>
            <p>
              <b>
                {row?.firstName || "-"} {row?.lastName || ""}
              </b>{" "}
              <span>{row?.email || ""}</span>
              <span>
                {row?.dialCode || "-"} {row?.phoneNo || "-"}
              </span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 160,
      field: "phone",
      headerName: translation.main.phone,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div>
            <p>{row?.dialCode || "-"} {row?.phoneNo || "-"}</p>
          </div>
        );
      },
    },
    {
      minWidth: 150,
      field: "ongoingJobs",
      headerName: translation.main.totalTasks,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p>
            <strong> {row?.totalTasks || "0"}</strong>
          </p>
        );
      },
    },
    {
      minWidth: 150,
      field: "completedTasks",
      headerName: translation.main.comJobs,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p>
            <strong> {row?.completedTasks || "0"}</strong>
          </p>
        );
      },
    },

    {
      minWidth: 150,
      field: "earnings",
      headerName: translation.main.totalEarning,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p className="c_success">
            <strong>AED {row?.totalEarnings || "0"}</strong>
          </p>
        );
      },
    },
    // {
    //   minWidth: 100,
    //   field: "status",
    //   headerName: translation.globals.status,
    //   editable: false,
    //   sortable: false,
    //   align: "center",
    //   headerAlign: "center",
    //   renderCell: ({ row }) => (
    //     <Switch
    //       {...label}
    //       size="small"
    //       checked={!row?.isBlocked}
    //       onChange={() => handleStatusUpdate(row?._id, !row?.isBlocked)}
    //     />
    //   ),
    // },
    {
      minWidth: 130,
      field: "action",
      headerName: translation.globals.actions,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions v2">
            <Box>
              <Tooltip title="View">
                <IconButton
                  onClick={() =>
                    navigate(
                      `/manage-freelancer/detail/${row?._id}?page=${page}`
                    )
                  }
                >
                  <img src="/static/images/eye_icon.svg" alt="" />
                </IconButton>
              </Tooltip>
            </Box>
            {isAdd ? (
              <Box>
                <Tooltip title="Edit">
                  <IconButton
                    onClick={() =>
                      navigate(`/manage-freelancer/edit/${row?._id}`)
                    }
                  >
                    <img src="/static/images/edit2_icon.svg" alt="" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}

            {isDelete ? (
              <Box>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => {
                      setSelectedId(row?._id);
                      setOpenDelete(true);
                    }}
                  >
                    <img src="/static/images/trash2_icon.svg" alt="" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
          </div>
        );
      },
    },
  ];

  const RejectedColumns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 240,
      field: translation.globals.name,
      headerName: "Name",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(`/manage-freelancer/detail/${row?._id}?page=${page}`)
            }
          >
            <figure>
              <img
                src={row?.image || "/static/images/user_placeholder.png"}
                alt=""
              />
            </figure>
            <p>
              <b>
                {row?.firstName || "-"} {row?.lastName || ""}
              </b>{" "}
              <span>{row?.email || ""}</span>
              <span>
                {row?.dialCode || "-"} {row?.phoneNo || "-"}
              </span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 160,
      field: "phone",
      headerName: translation.main.phone,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div>
            <p>{row?.dialCode || "-"} {row?.phoneNo || "-"}</p>
          </div>
        );
      },
    },
    {
      minWidth: 150,
      field: "ongoingJobs",
      headerName: translation.main.totalTasks,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p>
            <strong> {row?.totalTasks || "0"}</strong>
          </p>
        );
      },
    },
    {
      minWidth: 150,
      field: "completedTasks",
      headerName: translation.main.comJobs,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p>
            <strong> {row?.completedTasks || "0"}</strong>
          </p>
        );
      },
    },

    {
      minWidth: 150,
      field: "earnings",
      headerName: translation.main.totalEarning,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p className="c_success">
            <strong>AED {row?.totalEarnings || "0"}</strong>
          </p>
        );
      },
    },
    // {
    //   minWidth: 100,
    //   field: "status",
    //   headerName: translation.globals.status,
    //   editable: false,
    //   sortable: false,
    //   align: "center",
    //   headerAlign: "center",
    //   renderCell: ({ row }) => (
    //     <Switch
    //       {...label}
    //       size="small"
    //       checked={!row?.isBlocked}
    //       onChange={() => handleStatusUpdate(row?._id, !row?.isBlocked)}
    //     />
    //   ),
    // },
    {
      minWidth: 130,
      field: "action",
      headerName: translation.globals.actions,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions v2">
            <Box>
              <Tooltip title="View">
                <IconButton
                  onClick={() =>
                    navigate(
                      `/manage-freelancer/detail/${row?._id}?page=${page}`
                    )
                  }
                >
                  <img src="/static/images/eye_icon.svg" alt="" />
                </IconButton>
              </Tooltip>
            </Box>
            {isAdd ? (
              <Box>
                <Tooltip title="Edit">
                  <IconButton
                    onClick={() =>
                      navigate(`/manage-freelancer/edit/${row?._id}`)
                    }
                  >
                    <img src="/static/images/edit2_icon.svg" alt="" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}

            {isDelete ? (
              <Box>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => {
                      setSelectedId(row?._id);
                      setOpenDelete(true);
                    }}
                  >
                    <img src="/static/images/trash2_icon.svg" alt="" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (pageNumber) {
      setPage(Number(pageNumber));
    }
  }, [pageNumber]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{translation.main.manageFreelancer}</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translation.dashboard.dashboard}
          </span>{" "}
          - {translation.main.manageFreelancer}
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label={translation.main.pending} {...a11yProps(0)} />
              <Tab label={translation.main.approved} {...a11yProps(1)} />
              <Tab label={translation.main.rejected} {...a11yProps(2)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                placeholder={translation.user.SearchHere}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
          <div className="right_s">
            {/* {isAdd ? (
                     <Button
                       className="btn btn_primary"
                       onClick={() => navigate("/manage-customers/add")}
                     >
                       <AddIcon /> Add
                     </Button>
                   ) : null} */}
            {rows.length !== 0 && (
              <Button className="btn btn_grey" onClick={getExportCsv}>
                <DownloadIcon /> {translation.main.export}
              </Button>
            )}
          </div>
        </div>

        <CustomTabPanel value={valueTabs} index={0}>
          <div className="sorting_table">
            <TableComponent
              columns={pendingColumns}
              rows={rows}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={isLoading}
            />
            {rows.length !== 0 && (
              <div style={styles.tableFooter}>
                <div style={styles.paginationInfo}>
                  <span style={styles.totalRecords}>Showing {rows.length} of {count} records</span>
                </div>
              </div>
            )}
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          {/* <TeamList rows={rows} columns={ApprovedColumns} /> */}
          <div className="sorting_table">
            <TableComponent
              columns={ApprovedColumns}
              rows={rows}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={isLoading}
            />
            {rows?.length !== 0 && (
              <div style={styles.tableFooter}>
                <div style={styles.paginationInfo}>
                  <span style={styles.totalRecords}>Showing {rows.length} of {count} records</span>
                </div>
              </div>
            )}
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={2}>
          {/* <TeamList rows={rows} columns={RejectedColumns} /> */}
          <div className="sorting_table">
            <TableComponent
              columns={RejectedColumns}
              rows={rows}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={isLoading}
            />
            {rows.length !== 0 && (
              <div style={styles.tableFooter}>
                <div style={styles.paginationInfo}>
                  <span style={styles.totalRecords}>Showing {rows.length} of {count} records</span>
                </div>
              </div>
            )}
          </div>
        </CustomTabPanel>
      </div>
      <ConfirmationModal
        text={translation.error.warnFreelancer}
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
      />
      <FreelancerRejectionReasonModal
        open={openRejection}
        onClose={handleReject}
        setOpen={setOpenRejection}
        selectedId={selectedId}
        setRejectionRsn={setRejectionRsn}
        rejectionRsn={rejectionRsn}
      />
    </MainContainer>
  );
};

export default ManageMaintenanceTeam;
